import { yupResolver } from '@hookform/resolvers/yup';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { drawerClasses } from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { type ModelError_Response } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { Drawer } from '~components/Drawer/src/ui';
import ScanningOverlay from '~components/ScanningOverlay/ScanningOverlay';
import { trackTimesheetSaved } from '~hooks/useAnalytics/trackTimesheetSaved';
import { Timesheet, TimesheetMetadata } from '~hooks/useTimesheets/models';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import useTimesheetScanPolling from '~hooks/useTimesheets/useTimesheetScanPolling';
import { useStores } from '~store/RootStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { extractAxiosErrorInfo, extractFieldErrors } from '~utils/extractAxiosError';

import DrawerActions from './DrawerActions';
import DrawerFooter from './DrawerFooter';
import DrawerHeader from './DrawerHeader';
import TimesheetDetailsView from './TimesheetDetailsView';
import TimesheetImage, { UPLOAD_DROP_ZONE_HEIGHT } from './TimesheetImage';
import TimesheetImageHeader from './TimesheetImageHeader';
import { ALLOWED_IMAGE_EXTENSIONS, MAX_IMAGE_SIZE_IN_MB } from './timesheetImageUpload';
import {
  TimesheetFormValues,
  timesheetFormValuesToUpdate,
  timesheetSchema,
  timesheetToFormValues,
} from './timesheetSchema';

const BYTES_PER_MB = 1024 * 1024;
const DRAWER_HEADER_HEIGHT = '70px';
const DRAWER_FOOTER_HEIGHT = '62px';
const DRAWER_COLUMNS = 'minmax(0, 1fr) auto minmax(0, 1fr)';

// reset() inherits keepDirtyValues from useForm's resetOptions, which would keep the
// typed values these resets exist to drop.
const discardTypedValues = { keepDirtyValues: false };

const formFieldByApiField: Record<string, keyof TimesheetFormValues> = {
  break_minutes: 'breakMinutes',
  ended_at: 'endedAtTime',
  // The API derives net minutes as total minus break, so a net error means Break is
  // larger than Total.
  net_minutes: 'breakMinutes',
  started_at: 'startedAtTime',
  timesheet_number: 'timesheetNumber',
  total_minutes: 'totalHours',
};

interface TimesheetDrawerProps {
  companyId: string | undefined;
  isScanningTimesheet?: boolean;
  // Called after a save, an image upload or a delete succeeds
  onChanged?: () => void;
  onClose: () => void;
  // Opens the drawer on an empty drop zone when there is no timesheet yet; the caller creates
  // it from the slip and then passes the new record in as `timesheet`.
  onCreateFromImage?: (file: File) => Promise<void>;
  // Called once the AI scan of an image added in the drawer finishes.
  onScanned?: (timesheet: Timesheet) => void;
  open: boolean;
  timesheet: Timesheet | null;
}

export default function TimesheetDrawer({
  companyId,
  isScanningTimesheet = false,
  onChanged,
  onClose,
  onCreateFromImage,
  onScanned,
  open,
  timesheet,
}: TimesheetDrawerProps) {
  const {
    updateTimesheetById,
    deleteTimesheetById,
    getTimesheetMetadataById,
    uploadTimesheetImageById,
  } = useTimesheets();
  const { toasterStore, userStore } = useStores();
  const { canDeleteTicket, canEditTicket } = userStore.getPermissions();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const [metadata, setMetadata] = useState<TimesheetMetadata>();
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const isUploadingImageRef = useRef(false);
  const aiOutcomeTrackedTimesheetIdsRef = useRef(new Set<string>());
  const [savedTimesheet, setSavedTimesheet] = useState<Timesheet | null>(null);
  const [pendingScanTimesheetId, setPendingScanTimesheetId] = useState<string | null>(
    null,
  );

  // Closing mid-upload or mid-save would let the request land on a form the user has
  // already abandoned, so every way out waits for it.
  const isBusy = isSaving || isDeleting || isUploadingImage;

  const handleScanSettled = useCallback(
    (scannedTimesheet: Timesheet | null) => {
      setPendingScanTimesheetId(null);

      if (scannedTimesheet) {
        setSavedTimesheet(scannedTimesheet);
        onScanned?.(scannedTimesheet);
      }
    },
    [onScanned],
  );

  useTimesheetScanPolling({
    companyId,
    onSettled: handleScanSettled,
    timesheetId: pendingScanTimesheetId,
  });

  // Some callers hand the drawer a snapshot, so a saved number or uploaded image would not
  // appear until the drawer is reopened without this.
  const displayedTimesheet =
    savedTimesheet && savedTimesheet.id === timesheet?.id ? savedTimesheet : timesheet;

  // A newer record from the caller (e.g. once a scan finishes) or reopening the drawer
  // supersedes the local copy, which would otherwise mask it.
  useEffect(() => {
    setSavedTimesheet(null);
  }, [timesheet, open]);

  const isScanning =
    isScanningTimesheet ||
    (displayedTimesheet !== null && pendingScanTimesheetId === displayedTimesheet.id);

  const timesheetId = timesheet?.id;
  const aiProcessed = timesheet?.aiProcessed ?? false;

  // The AI-extracted structured data lives on a dedicated endpoint (not the list
  // payload) and only exists once the vision extraction has run, so fetch it lazily
  // when an AI-processed timesheet is shown.
  useEffect(() => {
    setMetadata(undefined);
    setIsLoadingMetadata(false);

    if (!open || !companyId || !timesheetId || !aiProcessed) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    setIsLoadingMetadata(true);

    const fetchMetadata = async () => {
      try {
        const result = await getTimesheetMetadataById(companyId, timesheetId, signal);

        if (!signal.aborted && result) {
          setMetadata(result);
        }
      } catch (error) {
        if (signal.aborted) return;

        throw error;
      } finally {
        if (!signal.aborted) {
          setIsLoadingMetadata(false);
        }
      }
    };

    void fetchMetadata();

    return () => controller.abort();
  }, [open, companyId, timesheetId, aiProcessed, getTimesheetMetadataById]);

  // With keepDirtyValues, react-hook-form writes kept dirty values into this object, so
  // it only feeds useForm; snapshots of the saved values are built fresh instead.
  const valuesForForm = useMemo(
    () => timesheetToFormValues(displayedTimesheet),
    [displayedTimesheet],
  );
  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { isDirty },
  } = useForm<TimesheetFormValues>({
    resolver: yupResolver(timesheetSchema),
    // An upload or scan response carries the server's values, which must not overwrite
    // ones the approver is still typing.
    resetOptions: { keepDirtyValues: true },
    values: valuesForForm,
  });

  useEffect(() => {
    setIsEditing(false);
    setIsConfirmingDiscard(false);
    reset(undefined, discardTypedValues);
  }, [open, timesheetId, reset]);

  // Each edit starts from the timesheet as it is now, not from what was typed last time:
  // a save can normalize values (hours rounded to whole minutes) without changing them.
  const handleEdit = useCallback(() => {
    reset(timesheetToFormValues(displayedTimesheet), discardTypedValues);
    setIsEditing(true);
  }, [displayedTimesheet, reset]);

  const handleCancelEdit = useCallback(() => {
    reset(timesheetToFormValues(displayedTimesheet), discardTypedValues);
    setIsEditing(false);
  }, [displayedTimesheet, reset]);

  const handleClose = useCallback(() => {
    if (isBusy) {
      return;
    }

    if (isEditing && isDirty) {
      setIsConfirmingDiscard(true);

      return;
    }

    onClose();
  }, [isBusy, isDirty, isEditing, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    setIsConfirmingDiscard(false);
    onClose();
  }, [onClose]);

  const trackAiOutcomeOnce = (resolvedTimesheet: Timesheet) => {
    if (aiOutcomeTrackedTimesheetIdsRef.current.has(resolvedTimesheet.id)) {
      return;
    }

    trackTimesheetSaved(resolvedTimesheet);
    aiOutcomeTrackedTimesheetIdsRef.current.add(resolvedTimesheet.id);
  };

  const onSubmit = async (submittedValues: TimesheetFormValues) => {
    if (!companyId || !timesheet || !displayedTimesheet) {
      return;
    }

    const attributes = timesheetFormValuesToUpdate(
      submittedValues,
      timesheetToFormValues(displayedTimesheet),
    );
    // Rounding to whole minutes or dropping seconds can turn an edit back into the saved
    // value, which leaves nothing to send.
    const hasChanges = Object.keys(attributes).length > 0;

    if (!hasChanges) {
      trackAiOutcomeOnce(displayedTimesheet);
      setIsEditing(false);

      return;
    }

    setIsSaving(true);

    try {
      const updatedTimesheet = await updateTimesheetById(
        companyId,
        timesheet.id,
        attributes,
        { showValidationErrorsInline: true },
      );
      trackAiOutcomeOnce(updatedTimesheet);
      // The response omits the job and driver-day summaries "Timesheet For" shows
      setSavedTimesheet(updatedTimesheet.withLinkedSummariesFrom(displayedTimesheet));
      setIsEditing(false);
      onChanged?.();
      toasterStore.push(alert(t('approvals.timesheets.saved'), AlertTypes.success));
    } catch (error) {
      if (!isAxiosError<ModelError_Response>(error) || error.response?.status !== 422) {
        throw error;
      }

      const fieldErrors = extractFieldErrors(error).filter(
        ({ field }) => formFieldByApiField[field],
      );

      fieldErrors.forEach(({ field, message }) => {
        setError(formFieldByApiField[field], { message, type: 'server' });
      });

      if (!fieldErrors.length) {
        const { message } = extractAxiosErrorInfo(
          error,
          t('error_messages.timesheets.failed_to_update'),
        );
        toasterStore.push(alert(message, AlertTypes.error));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageTypeError = () => {
    toasterStore.push(
      alert(
        t('ticket_drawer_v2.image_uploader.invalid_file_type_error', {
          extensions: ALLOWED_IMAGE_EXTENSIONS.join(', '),
        }),
        AlertTypes.error,
      ),
    );
  };

  const handleAddImage = async (file: File) => {
    if (isUploadingImageRef.current) {
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_IN_MB * BYTES_PER_MB) {
      toasterStore.push(
        alert(
          t('file_upload.max_size', { maxSize: `${MAX_IMAGE_SIZE_IN_MB} MB` }),
          AlertTypes.error,
        ),
      );

      return;
    }

    isUploadingImageRef.current = true;
    setIsUploadingImage(true);

    try {
      if (!timesheet) {
        await onCreateFromImage?.(file);

        return;
      }

      if (!companyId) {
        console.warn('Skipped uploading a timesheet image: no company id');

        return;
      }

      const updatedTimesheet = await uploadTimesheetImageById(
        companyId,
        timesheet.id,
        file,
      );
      setSavedTimesheet(updatedTimesheet.withLinkedSummariesFrom(displayedTimesheet));
      onChanged?.();

      if (!updatedTimesheet.aiProcessed) {
        setPendingScanTimesheetId(updatedTimesheet.id);
      }
    } catch {
      // uploadTimesheetImageById has already reported the failure
    } finally {
      isUploadingImageRef.current = false;
      setIsUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!companyId || !timesheet) {
      return;
    }

    setIsDeleting(true);

    try {
      await deleteTimesheetById(companyId, timesheet.id);
      setIsDeleteDialogOpen(false);
      onChanged?.();
      onClose();
    } catch {
      // deleteTimesheetById has already reported the failure
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Drawer
      onClose={handleClose}
      open={open}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          maxWidth: '1200px',
          width: '80%',
        },
      }}
    >
      {displayedTimesheet && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateAreas:
              '"header header header" "image divider actions" "image divider details" "image divider footer"',
            // minmax(0, …) lets a long value wrap instead of widening its column past the drawer.
            gridTemplateColumns: DRAWER_COLUMNS,
            gridTemplateRows: `${DRAWER_HEADER_HEIGHT} auto 1fr ${isEditing ? '0px' : DRAWER_FOOTER_HEIGHT}`,
            height: '100%',
          }}
        >
          <DrawerHeader
            isProcessing={isScanning}
            onClose={handleClose}
            sx={{ gridArea: 'header' }}
          />

          <Box sx={{ gridArea: 'image', overflow: 'auto', p: 3 }}>
            <TimesheetImageHeader
              fileUrl={displayedTimesheet.fileAttachment?.fileUrl ?? null}
              isBusy={isBusy}
              timesheetNumber={displayedTimesheet.timesheetNumber ?? null}
            />
            <TimesheetImage
              fileUrl={displayedTimesheet.fileAttachment?.fileUrl ?? null}
              isUploadingImage={isUploadingImage}
              mimeType={displayedTimesheet.fileAttachment?.mimeType ?? null}
              onAddImage={canEditTicket ? handleAddImage : undefined}
              onTypeError={handleImageTypeError}
              previewUrl={displayedTimesheet.fileAttachment?.previewUrl ?? null}
            />
          </Box>

          <Box sx={{ gridArea: 'divider' }}>
            <Divider orientation="vertical" />
          </Box>

          <DrawerActions
            canDelete={canDeleteTicket}
            canEdit={canEditTicket}
            isBusy={isBusy}
            isDirty={isDirty}
            isEditing={isEditing}
            isSaving={isSaving || isUploadingImage}
            onCancelEdit={handleCancelEdit}
            onDelete={() => setIsDeleteDialogOpen(true)}
            onEdit={handleEdit}
            onSave={handleSubmit(onSubmit)}
            sx={(theme) => ({
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              gridArea: 'actions',
              px: 2,
              py: 1.5,
            })}
          />

          <Box sx={{ gridArea: 'details', minHeight: 0, position: 'relative' }}>
            {isScanning && (
              <ScanningOverlay label={t('approvals.timesheets.scanning_message')} />
            )}
            <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
              <TimesheetDetailsView
                control={control}
                isEditing={isEditing}
                isLoadingMetadata={isLoadingMetadata}
                metadata={metadata}
                setValue={setValue}
                timesheet={displayedTimesheet}
              />
            </Box>
          </Box>

          {!isEditing && (
            <DrawerFooter
              isBusy={isBusy}
              onClose={handleClose}
              sx={{ gridArea: 'footer' }}
            />
          )}
        </Box>
      )}

      {!displayedTimesheet && onCreateFromImage && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateAreas:
              '"header header header" "image divider details" "image divider footer"',
            gridTemplateColumns: DRAWER_COLUMNS,
            gridTemplateRows: `${DRAWER_HEADER_HEIGHT} 1fr ${DRAWER_FOOTER_HEIGHT}`,
            height: '100%',
          }}
        >
          <DrawerHeader
            isProcessing={false}
            onClose={handleClose}
            sx={{ gridArea: 'header' }}
          />

          <Box sx={{ gridArea: 'image', overflow: 'auto', p: 3 }}>
            <TimesheetImage
              fileUrl={null}
              isUploadingImage={isUploadingImage}
              mimeType={null}
              onAddImage={handleAddImage}
              onTypeError={handleImageTypeError}
              previewUrl={null}
            />
          </Box>

          <Box sx={{ gridArea: 'divider' }}>
            <Divider orientation="vertical" />
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              gridArea: 'details',
              height: UPLOAD_DROP_ZONE_HEIGHT,
              justifyContent: 'center',
              m: 3,
              textAlign: 'center',
            }}
          >
            <AutoAwesome sx={{ color: 'text.disabled', fontSize: 48 }} />
            <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
              {t('approvals.timesheets.empty_title')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 420 }}>
              {t('approvals.timesheets.empty_subtitle')}
            </Typography>
          </Box>

          <DrawerFooter
            isBusy={isBusy}
            onClose={handleClose}
            sx={{ gridArea: 'footer' }}
          />
        </Box>
      )}

      <ConfirmationDialog
        isBusy={isDeleting}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('approvals.timesheets.delete_confirm_title')}
      >
        {t('approvals.timesheets.delete_confirm_message')}
      </ConfirmationDialog>

      <ConfirmationDialog
        cancelButtonProps={{ children: t('actions.no') }}
        confirmButtonProps={{ children: t('actions.exit'), color: 'primary' }}
        isOpen={isConfirmingDiscard}
        onClose={() => setIsConfirmingDiscard(false)}
        onConfirm={handleConfirmDiscard}
        title={t('project.confirm_exit_title')}
      >
        {t('project.confirm_exit_text')}
      </ConfirmationDialog>
    </Drawer>
  );
}
